import React, { memo } from "react";
import * as PropTypes from "prop-types";
import { databasePropType } from "../../../propTypes";
import AsynchronousDataWidget from "../../widgets/AsynchronousDataWidget";
import ConsumptionCostChartWidget from "./ConsumptionCostChartWidget";
import CostVersusUsersCountryBox from "./CostVersusUsersCountryBox";
import MethodMixOfUseVsCostBySectorChartWidget from "./MethodMixOfUseVsCostBySectorChartWidget";
import MethodMixOfUseVsCostChartWidget from "./MethodMixOfUseVsCostChartWidget";
import ShareOfConsumptionCostsByMethodChartWidget from "./ShareOfConsumptionCostsByMethodChartWidget";
import ShareOfConsumptionCostsVsUsersBySectorChartWidget from "./ShareOfConsumptionCostsVsUsersBySectorChartWidget";
import ShareOfUsersVsCostChartWidget from "./ShareOfUsersVsCostChartWidget";
import SmallFigure from "../layout/SmallFigure";
import ThemeIntroductionController from "../layout/ThemeIntroductionController";
import LargeFigure from "../layout/LargeFigure";
import FigureOnly from "../layout/FigureOnly";

const Theme4 = props => {
  const database = props.database;
  const data = props.data;

  return (
    <section id="theme4" className="theme">
      <ThemeIntroductionController
        reference="04"
        title="Differences in the Distribution of Users&nbsp;versus&nbsp;Costs"
      >
        Consumption cost is not simply a function of use. The differences in the
        method mix of users and the method mix of costs are due to a number of
        factors, including differences in the number of units per year of each
        method a user must consume; differences in costs between methods; and
        differences in cost for each method from country to country. By
        understanding the relationship between users and costs, the reproductive
        health community can ensure resources are adequately distributed such
        that women can continue to access a full range of contraceptive methods.
      </ThemeIntroductionController>

      {/*Figure 4.1*/}
      <SmallFigure
        figure={
          <AsynchronousDataWidget loaded={!!data}>
            <MethodMixOfUseVsCostChartWidget
              database={database}
              data={data ? data.methodMixOfUseVsCostChart : []}
              onChange={props.onWidgetChange}
            />
          </AsynchronousDataWidget>
        }
      >
        <p>
          The total consumption cost of contraceptive supplies in LMICs is $3.45
          billion. Unlike users, where the proportion using LAPMs versus STMs is
          fairly even, the method mix in terms of costs is dominated by STMs,
          comprising 91% of the total consumption cost. For example, with a
          total of $2.38 billion, pills are by far the greatest contributor to
          consumption cost, making up 69% of the total, yet they account for
          only 20% of users. In contrast, IUDs account for 11% of users but only
          3% of costs, while implants make up a similar share of both use and
          cost, at 4% and 3%, respectively.
        </p>
      </SmallFigure>

      {/*Box 4.1*/}
      <FigureOnly>
        <CostVersusUsersCountryBox />
      </FigureOnly>

      {/*Figure 4.3*/}
      <SmallFigure
        figure={
          <AsynchronousDataWidget loaded={!!data}>
            <ConsumptionCostChartWidget
              database={database}
              data={data ? data.consumptionCostChart : []}
              onChange={props.onWidgetChange}
            />
          </AsynchronousDataWidget>
        }
      >
        <p>
          While each sector has a distinct landscape in terms of method use,
          even greater differences appear when comparing consumption costs
          across sectors. Overall, the majority of costs (79%) derive from users
          purchasing supplies from private sector entities, even though these
          same clients account for only 43% of users. By contrast, only 21% of
          costs ($738 million) comes from the public sector despite the fact
          that most users get their supplies from public entities. This
          pronounced disparity is due to the preponderance of STMs (which
          require frequent resupply and therefore greater volumes) in the
          private sector, coupled with the fact that private sector consumers
          often pay higher prices than public sector procurers for the same
          method.
        </p>
      </SmallFigure>

      {/*Figure 4.4*/}
      <SmallFigure
        figure={
          <AsynchronousDataWidget loaded={!!data}>
            <MethodMixOfUseVsCostBySectorChartWidget
              database={database}
              data={data ? data.methodMixOfUseVsCostBySectorChart : []}
              onChange={props.onWidgetChange}
            />
          </AsynchronousDataWidget>
        }
        figureOnLeft={true}
      >
        <p>
          Within the public sector LAPMs account for two-thirds of users, but
          only 29% of costs. This is because STMs are relatively costly per user
          even in the public sector. Injectables and pills represent 38% and 26%
          of the public sector cost method mix, despite representing only 14%
          and 12% of the public sector use method mix.
        </p>
        <p>
          Greater variability in the price paid by private sector consumers
          means that differences in use and cost by method are even more
          pronounced within the private sector. Most private sector costs come
          from pill consumption (81%) despite pills representing only 29% of the
          private sector use mix.
        </p>
      </SmallFigure>

      {/*Figure 4.5*/}
      <SmallFigure
        figure={
          <AsynchronousDataWidget loaded={!!data}>
            <ShareOfUsersVsCostChartWidget
              database={database}
              data={data ? data.shareOfUsersVsCostChart : []}
              onChange={props.onWidgetChange}
            />
          </AsynchronousDataWidget>
        }
      >
        <p>
          The distinct landscape of users compared to that of costs can also be
          seen within each GNI group. Divergent method mixes and price points
          across the three GNI groups means that the distribution of costs both
          across and within each GNI group looks different. While
          lower-middle-income countries are home to the majority of users, the
          highest consumption costs are actually found in upper middle-income
          countries. Two-thirds of the total consumption cost comes from
          upper-middle-income countries despite only accounting for 30% of
          users. The share of costs from lower-middle-income countries (27%) is
          less than half of the share of users (61%). Low-income countries
          account for 6% of costs compared to 10% of users.{" "}
        </p>
      </SmallFigure>

      {/*Figure 4.6*/}
      <SmallFigure
        figure={
          <AsynchronousDataWidget loaded={!!data}>
            <ShareOfConsumptionCostsByMethodChartWidget
              data={data ? data.shareOfConsumptionCostsByMethodChart : []}
            />
          </AsynchronousDataWidget>
        }
        figureOnLeft={true}
      >
        <p>
          Within upper-middle-income countries, pill consumption accounts for
          the lion’s share of total costs (82%). Pills are also the largest
          contributor to costs in lower-middle-income countries, though they
          represent a much smaller share (46%). Within low-income countries
          injectables represent the largest share of costs (47%).
        </p>
        <p>
          A quarter of all spending on implants takes place in low-income
          countries, while nearly three-quarters of spending on sterilization
          comes from lower-middle-income countries, and only 38% of spending on
          injectables comes from upper-middle-income countries.
        </p>
      </SmallFigure>

      {/*Figure 4.7*/}
      <LargeFigure
        figure={
          <AsynchronousDataWidget loaded={!!data}>
            <ShareOfConsumptionCostsVsUsersBySectorChartWidget
              database={database}
              data={
                data ? data.shareOfConsumptionCostsVsUsersBySectorChart : []
              }
              onChange={props.onWidgetChange}
            />
          </AsynchronousDataWidget>
        }
      >
        <p>
          As is the case with users, the public sector plays a comparatively
          smaller role across each income group when looking at costs; however,
          these differences are much more pronounced&mdash;from 69% within
          low-income countries to only 10% within upper-middle-income countries.
          The difference between the share of public sector users and public
          sector costs is most stark within upper-middle-income countries; the
          public sector accounts for 50% of users but only 10% of costs.
          Discrepancies are also seen in lower-middle-income-countries; in this
          GNI group the public sector accounts for 59% of users by only 38% of
          costs. Within low-income countries the public sector plays a similar
          role in both use (70%) and cost (69%) due to differences in method mix
          and prices in this GNI group.
        </p>
        <p>
          Making comparisons between the distribution of users and costs can be
          helpful in planning adequately for the level of funding and effort
          needed to sustain contraceptive use in different settings. Ensuring
          all women have access to a full range of contraceptive methods and are
          able to use the method they chose is critical to ensure rights and
          choice. This analysis of use versus cost is not to suggest an optimal
          method mix or advocate for a shift to lower cost methods. Rather, it
          is meant to illustrate the importance of understanding complexities
          behind the contraceptive market&mdash;and the utility of looking at
          the market from multiple perspectives.
        </p>
      </LargeFigure>
    </section>
  );
};

Theme4.propTypes = {
  database: databasePropType,
  data: PropTypes.object,
  onWidgetChange: PropTypes.func.isRequired,
  onDownloadCsv: PropTypes.func.isRequired
};

export default memo(Theme4);
